import DeselectIcon from "@mui/icons-material/Deselect";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import Alert from "@mui/material/Alert";
import MenuItem from "@mui/material/MenuItem";
import * as React from "react";

import { useAuth, useCurrentOrgId } from "@/providers/auth/hooks";
import { RobotoStyledPopoverMenu } from "@/shared/components/RobotoStyledPopoverMenu";
import { EventDetailFields } from "@/shared/domain/events/EventDetailFields";
import { EventRecord } from "@/shared/domain/events/EventRecord";
import { eventsTableColumns } from "@/shared/domain/events/EventsTableColumns";
import { eventQueryKeys } from "@/shared/domain/events/hooks";
import { QueryContentMode, QueryTarget } from "@/shared/domain/query";
import {
  useQueryCount,
  usePaginatedQueryResults,
  useStructuredQuery,
} from "@/shared/domain/query/hooks";
import { useEventTablePreferences } from "@/shared/domain/users/hooks";
import { Button } from "@/shared/foundation/primitives";
import {
  DataTable,
  useDataTableState,
  type DataTableLoadingState,
} from "@/shared/foundation/primitives/Table";
import type { Condition, SearchQueryBody, SortDirection } from "@/types";

import styles from "./EventsList.module.css";

export interface SelectionOption<T> {
  name: string;
  icon?: React.ReactNode;
  action: (args: { selected: T[] }) => void | Promise<void>;
}

interface EventsListProps {
  conditions?: Condition[];
  selectionOptions?: SelectionOption<EventRecord>[];
}

export function EventsList({ conditions, selectionOptions }: EventsListProps) {
  const { currentOrganization } = useAuth();
  const resolvedOrgId = useCurrentOrgId(currentOrganization?.org_id);
  const { preferences, savePreferences } = useEventTablePreferences();

  const tableState = useDataTableState({
    initialSort: { columnId: "start_time", direction: "desc" },
    preferences,
    onPreferencesChange: savePreferences,
  });

  const [actionAnchorEl, setActionAnchorEl] =
    React.useState<HTMLElement | null>(null);

  // Accumulates every row seen across page loads so cross-page selections
  // resolve to full records when an action fires.
  const rowMapRef = React.useRef(new Map<string, EventRecord>());

  let condition: Condition | undefined;
  if (conditions?.length === 1) {
    condition = conditions[0];
  } else if (conditions?.length) {
    condition = { conditions, operator: "AND" as const };
  }

  const query: SearchQueryBody = {
    ...(condition && { condition }),
    ...(tableState.sort && {
      sort_by: tableState.sort.columnId,
      sort_direction: tableState.sort.direction.toUpperCase() as SortDirection,
    }),
  };

  const submitQuery = useStructuredQuery({
    resolvedOrgId,
    query,
    target: QueryTarget.Events,
    contentMode: QueryContentMode.RecordOnly,
    queryKey: eventQueryKeys.list(resolvedOrgId, query),
  });
  const queryId = submitQuery.data?.query_id;

  const pageQuery = usePaginatedQueryResults<EventRecord>(
    queryId,
    tableState.currentPage,
    tableState.pageSize,
  );
  const countQuery = useQueryCount(queryId);

  const rows = pageQuery.data ?? [];

  React.useEffect(() => {
    for (const row of pageQuery.data ?? []) {
      rowMapRef.current.set(row.event_id, row);
    }
  }, [pageQuery.data]);

  const handleOptionClick = React.useCallback(
    async (option: SelectionOption<EventRecord>) => {
      setActionAnchorEl(null);
      const selectedRows = Array.from(tableState.selectedIds)
        .map((id) => rowMapRef.current.get(id))
        .filter((r): r is EventRecord => r !== undefined);
      try {
        await option.action({ selected: selectedRows });
      } finally {
        tableState.deselectAll();
      }
    },
    [tableState],
  );

  const renderExpandedRow = React.useCallback(
    (row: EventRecord) => <EventDetailFields event={row} />,
    [],
  );

  let loading: DataTableLoadingState = "idle";
  if (submitQuery.isLoading || pageQuery.isLoading) {
    loading = "foreground";
  } else if (submitQuery.isFetching || pageQuery.isFetching) {
    loading = "background";
  }
  const fetchError = submitQuery.error ?? pageQuery.error;

  const hasSelectionOptions = Boolean(selectionOptions?.length);
  const actionMenuOpen = Boolean(actionAnchorEl);

  return (
    <>
      {fetchError && (
        <Alert severity="error" className={styles.errorBanner}>
          {fetchError.message}
        </Alert>
      )}
      {hasSelectionOptions && tableState.selectedIds.size > 0 && (
        <div className={styles.actionsBar}>
          <Button
            id="events-selection-button"
            aria-controls={actionMenuOpen ? "events-selection-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={actionMenuOpen ? "true" : undefined}
            variant="contained"
            disableElevation
            size="small"
            endIcon={<KeyboardArrowDownIcon />}
            onClick={(e) => setActionAnchorEl(e.currentTarget)}
          >
            Selected ({tableState.selectedIds.size})
          </Button>
          <RobotoStyledPopoverMenu
            id="events-selection-menu"
            MenuListProps={{ "aria-labelledby": "events-selection-button" }}
            anchorEl={actionAnchorEl}
            open={actionMenuOpen}
            onClose={() => setActionAnchorEl(null)}
          >
            <MenuItem
              disableRipple
              onClick={() => {
                setActionAnchorEl(null);
                tableState.deselectAll();
              }}
            >
              <DeselectIcon fontSize="small" />
              Deselect All
            </MenuItem>
            {selectionOptions!.map((option) => (
              <MenuItem
                key={option.name}
                disableRipple
                onClick={() => {
                  void handleOptionClick(option);
                }}
              >
                {option.icon}
                {option.name}
              </MenuItem>
            ))}
          </RobotoStyledPopoverMenu>
        </div>
      )}
      <DataTable.Root>
        <DataTable.Table<EventRecord>
          {...tableState.table}
          hasNextPage={pageQuery.hasNextPage}
          loading={loading}
          columns={eventsTableColumns}
          data={rows}
          getRowId={(row) => row.event_id}
          noRowsContent="No events"
          totalRowCount={countQuery.data}
          selectionEnabled={hasSelectionOptions}
          expansion={{ renderRow: renderExpandedRow }}
        />
      </DataTable.Root>
    </>
  );
}
